import type { ElementType, ReactNode } from 'react';
import type { NavigationPageItem } from '@toolpad/core/AppProvider';
import * as Icons from '@locus-taxy/encore-mui/icons';

import type {
  ModuleItem,
  EntityLeaf,
  AppLeaf,
  FrontendRouteLeaf,
  NavNode,
} from '../api/endpoints/useModules';
import { RoutePath } from '../types/categories';
import { buildClientPrefixedPath } from './url';

interface NavigationBuildOptions {
  clientId: string;
  parentSegments: string[];
  parentLabels: string[];
  leaves: ModuleLeafRoute[];
  /** Track group nodes by their computed path to merge duplicates without altering URLs. */
  groupRegistry: Map<string, NavigationPageItem>;
  /** Track full paths to avoid pushing duplicate navigation items. */
  pathSet: Set<string>;
}

export interface ModuleLeafRoute {
  type: 'ENTITY' | 'APP' | 'FRONTEND_ROUTE';
  /**
   * Ordered list of ID segments that compose the full path for this leaf.
   * The last entry is the leaf's own identifier used in the URL.
   */
  pathSegments: string[];
  /** Name of the dynamic segment for the leaf in the route definition. */
  paramName: 'entityName' | 'appName' | 'routePath';
  label: string;
  /** The concrete value that will appear in the URL for the leaf node. */
  leafSegment: string;
  entityName?: string;
  appId?: string;
  route?: string;
  /**
   * Display labels of ancestor navigation groups for this leaf, in order from root to parent.
   * Used for DOM-independent active-parent highlighting when descendants are collapsed.
   */
  ancestorLabels: string[];
  /**
   * The full path as expected by Toolpad's internal router (including group segments and backtracking).
   * Used to force active state highlighting when the real URL is flat.
   */
  toolpadPath: string;
}

export interface ModulesNavigationResult {
  navigation: NavigationPageItem[];
  leaves: ModuleLeafRoute[];
  /** Map of path segment chains (joined by '/') to their display labels for nav nodes. */
  pathLabelMap: Record<string, string>;
}

export function buildModulesNavigation(
  items: ModuleItem[] | undefined,
  clientId: string
): ModulesNavigationResult {
  const leaves: ModuleLeafRoute[] = [];
  const pathLabelMap: Record<string, string> = {};
  const navigation = buildNavigationItems(items ?? [], {
    clientId,
    parentSegments: [],
    parentLabels: [],
    leaves,
    groupRegistry: new Map(),
    pathSet: new Set(),
    pathLabelMap,
  });

  return { navigation, leaves, pathLabelMap };
}

export function findFirstLeafPath(leaves: ModuleLeafRoute[]): string | null {
  if (!leaves.length) {
    return null;
  }

  return buildPathFromSegments(leaves[0].pathSegments);
}

function renderIconByName(iconName?: string, fallback?: ReactNode) {
  if (iconName) {
    const iconMap = Icons as unknown as Record<string, ElementType>;
    const Direct = iconMap[iconName];
    if (Direct) {
      return <Direct fontSize="medium" sx={{ color: 'inherit' }} />;
    }
    const WithSuffix = iconMap[`${iconName}Icon`];
    if (WithSuffix) {
      return <WithSuffix fontSize="medium" sx={{ color: 'inherit' }} />;
    }
  }
  return fallback ?? null;
}

function getBacktrackPrefix(depth: number): string {
  return depth > 0 ? '../'.repeat(depth) : '';
}

function buildNavigationItems(
  items: ModuleItem[],
  options: NavigationBuildOptions & { pathLabelMap: Record<string, string> }
): NavigationPageItem[] {
  const {
    clientId,
    parentSegments,
    parentLabels,
    leaves,
    pathLabelMap,
    groupRegistry,
    pathSet,
  } = options;
  const result: NavigationPageItem[] = [];

  const addLeafNode = (
    targetPath: string,
    title: string,
    icon: string | undefined,
    defaultIcon: ReactNode,
    leafData: Omit<ModuleLeafRoute, 'toolpadPath'>
  ) => {
    // Check global uniqueness based on the target URL
    if (pathSet.has(targetPath)) {
      return;
    }
    pathSet.add(targetPath);

    // Construct segment: backtrack out of parent groups + target path (without leading slash)
    // e.g. parents=['ops'], target='/clientId/...' -> '../clientId/...'
    const backtrack = getBacktrackPrefix(parentSegments.length);
    const segment = `${backtrack}${targetPath.slice(1)}`;

    // Calculate the Toolpad-internal path by joining parent segments and the leaf segment
    // Note: parentSegments contains the group labels/IDs used as segments
    const toolpadPath = buildPathFromSegments([...parentSegments, segment]);

    result.push({
      segment,
      title,
      icon: renderIconByName(icon, defaultIcon),
    });

    leaves.push({
      ...leafData,
      toolpadPath,
    });
  };

  items.forEach(item => {
    if (isNavNode(item)) {
      const navKeySegment = normalizeSegment(item.id ?? item.key ?? item.label);
      const keySegments = navKeySegment
        ? appendSegment(parentSegments, navKeySegment)
        : parentSegments.slice();
      const fullPath = buildPathFromSegments(keySegments);
      const nextLabels = [...parentLabels, item.label];
      // Record the label for this path depth to rebuild breadcrumbs later
      const key = keySegments.join('/');
      if (key) {
        pathLabelMap[key] = item.label;
      }
      const children = buildNavigationItems(item.items ?? [], {
        clientId,
        parentSegments: keySegments,
        parentLabels: nextLabels,
        leaves,
        groupRegistry,
        pathSet,
        pathLabelMap,
      });

      const existingGroup = groupRegistry.get(fullPath);
      if (existingGroup) {
        existingGroup.children = [
          ...(existingGroup.children ?? []),
          ...children,
        ];
      } else if (children.length > 0) {
        // Use the label/id as segment to ensure uniqueness and prevent "Duplicate path" warnings.
        // Child items will use "../" prefixes to backtrack out of these group segments
        // so that the final URL matches the flat route structure.
        const groupItem: NavigationPageItem = {
          segment: navKeySegment ?? item.label,
          title: item.label,
          icon: renderIconByName(
            item.icon,
            <Icons.Folder fontSize="medium" sx={{ color: 'inherit' }} />
          ),
          children,
        };
        groupRegistry.set(fullPath, groupItem);
        result.push(groupItem);
      } else {
        // No existing group and no children to register
      }
      return;
    }

    if (isEntityLeaf(item)) {
      const entityPath = `${RoutePath.ENTITIES}/${item.entityName}`;
      const targetPath = buildClientPrefixedPath(entityPath, clientId);
      const title = item.label ?? item.entityName;

      addLeafNode(
        targetPath,
        title,
        item.icon,
        <Icons.Dataset fontSize="medium" sx={{ color: 'inherit' }} />,
        {
          type: 'ENTITY',
          pathSegments: [
            RoutePath.ENTITIES.replace(/^\//, ''),
            item.entityName,
          ],
          paramName: 'entityName',
          label: title,
          leafSegment: item.entityName,
          entityName: item.entityName,
          ancestorLabels: parentLabels.slice(),
        }
      );
      return;
    }

    if (isAppLeaf(item)) {
      const appPath = `${RoutePath.CUSTOM_APPS}/${item.appId}`;
      const targetPath = buildClientPrefixedPath(appPath, clientId);
      const title = item.appOverrides?.name ?? item.label ?? item.appId;

      addLeafNode(
        targetPath,
        title,
        item.icon,
        <Icons.AppShortcut fontSize="medium" sx={{ color: 'inherit' }} />,
        {
          type: 'APP',
          pathSegments: [RoutePath.CUSTOM_APPS.replace(/^\//, ''), item.appId],
          paramName: 'appName',
          label: title,
          leafSegment: item.appId,
          appId: item.appId,
          ancestorLabels: parentLabels.slice(),
        }
      );
      return;
    }

    if (isFrontendRouteLeaf(item)) {
      const normalizedLeaf = item.route;
      const title = item.label ?? 'Frontend Route';
      const targetPath = buildClientPrefixedPath(item.route, clientId);

      addLeafNode(
        targetPath,
        title,
        item.icon,
        <Icons.Route fontSize="medium" sx={{ color: 'inherit' }} />,
        {
          type: 'FRONTEND_ROUTE',
          pathSegments: [targetPath.slice(1)],
          paramName: 'routePath',
          label: title,
          leafSegment: normalizedLeaf,
          route: item.route,
          ancestorLabels: parentLabels.slice(),
        }
      );
    }
  });

  return result;
}

function isNavNode(item: ModuleItem): item is NavNode {
  return Boolean((item as NavNode).items);
}

function isEntityLeaf(item: ModuleItem): item is EntityLeaf {
  return (item as EntityLeaf).type === 'ENTITY';
}

function isAppLeaf(item: ModuleItem): item is AppLeaf {
  return (item as AppLeaf).type === 'APP';
}

function isFrontendRouteLeaf(item: ModuleItem): item is FrontendRouteLeaf {
  return (item as FrontendRouteLeaf).type === 'FRONTEND_ROUTE';
}

function appendSegment(
  segments: string[],
  candidate?: string | null
): string[] {
  const normalized = normalizeSegment(candidate);
  if (!normalized) {
    return segments.slice();
  }
  return [...segments, normalized];
}

function normalizeSegment(segment?: string | null): string | null {
  if (!segment) {
    return null;
  }

  const trimmed = segment.trim().replace(/^\/+|\/+$/g, '');
  if (!trimmed) {
    return null;
  }

  return trimmed;
}

function buildPathFromSegments(segments: string[]): string {
  if (!segments.length) {
    return '/';
  }

  return `/${segments.join('/')}`;
}

/**
 * Strip the optional `/clientId/:clientId` prefix from a pathname.
 */
export function stripClientPrefix(pathname: string): string {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return path.replace(/^\/clientId\/[A-Za-z0-9_-]+(\/|$)/, '/');
}

/**
 * Find the best matching leaf (longest prefix match) for a given clientless path.
 */
export function findMatchingLeaf(
  pathWithoutClient: string,
  leaves: ModuleLeafRoute[]
): ModuleLeafRoute | null {
  let best: ModuleLeafRoute | null = null;
  let bestLen = -1;
  for (const leaf of leaves) {
    const prefix = `/${leaf.pathSegments.join('/')}`;
    if (
      pathWithoutClient === prefix ||
      pathWithoutClient.startsWith(`${prefix}/`)
    ) {
      if (leaf.pathSegments.length > bestLen) {
        best = leaf;
        bestLen = leaf.pathSegments.length;
      }
    }
  }
  return best;
}

export interface BreadcrumbItem {
  title: string;
}

/**
 * Build breadcrumb titles from the modules navigation for the current pathname.
 * This returns only titles, suitable for PageContainer.breadcrumbs.
 * Uses the module hierarchy (ancestorLabels) instead of URL path segments.
 */
export function buildBreadcrumbsFromModules(
  pathname: string,
  leaves: ModuleLeafRoute[]
): BreadcrumbItem[] {
  const clientless = stripClientPrefix(pathname);
  const match = findMatchingLeaf(clientless, leaves);
  if (!match) {
    return [];
  }

  // Use ancestorLabels (module hierarchy) + leaf label for breadcrumbs
  const items: BreadcrumbItem[] = match.ancestorLabels.map(label => ({
    title: label,
  }));
  items.push({ title: match.label });

  return items;
}
